import { useEffect } from "react";
import QuincyHeroSection from "./components/HeroSection";
import QuincyStickyNav from "./components/StickyNav";
import QuincyProcessingTimeSection from "./components/ProcessingTimeSection";
import QuincyServicesSection from "./components/ServicesSection";
import QuincyLocalAreaSection from "./components/LocalAreaSection";
import QuincyTestimonialsSection from "./components/TestimonialsSection";
import QuincyAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import QuincyComparisonSection from "./components/ComparisonSection";
import QuincyMistakesSection from "./components/MistakesSection";
import QuincyHowItWorksSection from "./components/HowItWorksSection";
import QuincyFAQSection, { faqs } from "./components/FAQSection";
import QuincyHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import QuincyFinalCTA from "./components/FinalCTA";

export default function QuincyPage() {
  useEffect(() => {
    document.title = "Emergency Passport Quincy, FL — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/quincy/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Quincy, FL — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Gadsden County and North Florida.");
    setMeta("geo.placename", "Quincy, Florida");
    setMeta("geo.region", "US-FL");
    setMeta("geo.position", "30.5885;-84.5832");
    setMeta("ICBM", "30.5885, -84.5832");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Quincy — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Quincy",
          containedInPlace: {
            "@type": "State",
            name: "Florida",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Quincy",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <QuincyHeroSection />
      <QuincyStickyNav />
      <QuincyProcessingTimeSection />
      <QuincyServicesSection />
      <QuincyLocalAreaSection />
      <QuincyTestimonialsSection />
      <QuincyAcceptanceFacilitiesSection />
      <QuincyComparisonSection />
      <QuincyMistakesSection />
      <QuincyHowItWorksSection />
      <QuincyFAQSection />
      <QuincyHelpfulResourcesSection />
      <QuincyFinalCTA />
    </div>
  );
}